import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { saveShowcaseItems } from '../utils/firebase';
import { RANKS, BASE_ELO } from '../constants/ranks';
import { LOOTBOX_RARITIES, STATTRAK_LABELS, getS2ItemValue, getLootboxRarities, getRarityTiers } from '../constants/lootbox';
import { STOCKS } from '../constants/stocks';
import ItemIcon from './lootbox/ItemIcon';
import { getRank, compositeScore, VfxSpan } from '../utils/helpers';
import { levelFromTotalXp } from '../constants/xp';
import { VFX_EFFECTS } from '../constants/vfx';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import EloDetailsModal from './EloDetailsModal';
import { AchievementCategory } from './AchievementsPage';
import { getAchievementCategories, getTotalAchievements } from '../constants/achievements';
import { SubTabs } from './ui';

const PROFILE_TABS = [
  { id: 'elo',          label: 'ELO' },
  { id: 'game',         label: 'Game Stats' },
  { id: 'casino',       label: 'Casino' },
  { id: 'unboxing',     label: 'Unboxing' },
  { id: 'showcase',     label: 'Showcase' },
  { id: 'achievements', label: 'Achievements' },
  { id: 'stocks',       label: 'Stocks' },
];

function getProfileTimeFilters(seasonConfig) {
  const filters = [{ id: 'all', label: 'All Time' }];
  const seasons = seasonConfig?.seasons || {};
  const currentSeason = seasonConfig?.currentSeason || 1;
  for (const sNum of Object.keys(seasons).sort((a, b) => parseInt(b) - parseInt(a))) {
    const s = parseInt(sNum);
    filters.push({
      id: `season_${s}`,
      label: s === currentSeason ? 'This Season' : `Season ${s}`,
    });
  }
  filters.push(
    { id: 'month', label: 'Past Month' },
    { id: 'week', label: 'Past Week' },
    { id: 'day', label: 'Today' },
  );
  return filters;
}

function getEloSeasonFilters(seasonConfig) {
  const filters = [];
  const seasons = seasonConfig?.seasons || {};
  const currentSeason = seasonConfig?.currentSeason || 1;
  for (const sNum of Object.keys(seasons).sort((a, b) => parseInt(b) - parseInt(a))) {
    const s = parseInt(sNum);
    filters.push({
      id: `season_${s}`,
      label: s === currentSeason ? 'This Season' : `Season ${s}`,
    });
  }
  if (filters.length === 0) filters.push({ id: 'season_1', label: 'Season 1' });
  return filters;
}

function filterByTime(games, filter, seasonConfig) {
  if (filter === 'all') return games;
  const now = Date.now();
  if (filter === 'day') return games.filter(g => now - g.timestamp < 24 * 60 * 60 * 1000);
  if (filter === 'week') return games.filter(g => now - g.timestamp < 7 * 24 * 60 * 60 * 1000);
  if (filter === 'month') return games.filter(g => now - g.timestamp < 30 * 24 * 60 * 60 * 1000);
  if (filter.startsWith('season_')) {
    const seasonNum = parseInt(filter.replace('season_', ''));
    return games.filter(g => g.season === seasonNum);
  }
  return games;
}

function filterByTimestamp(items, filter, seasonConfig, tsField = 'timestamp') {
  if (filter === 'all') return items;
  const now = Date.now();
  if (filter === 'day') return items.filter(i => now - (i[tsField] || 0) < 24 * 60 * 60 * 1000);
  if (filter === 'week') return items.filter(i => now - (i[tsField] || 0) < 7 * 24 * 60 * 60 * 1000);
  if (filter === 'month') return items.filter(i => now - (i[tsField] || 0) < 30 * 24 * 60 * 60 * 1000);
  if (filter.startsWith('season_')) {
    const seasonNum = parseInt(filter.replace('season_', ''));
    // Prefer season field, fall back to timestamp range from seasonConfig
    return items.filter(i => {
      if (i.season != null) return i.season === seasonNum;
      const seasons = seasonConfig?.seasons || {};
      const seasonData = seasons[String(seasonNum)];
      if (!seasonData) return false;
      const ts = i[tsField] || 0;
      const start = seasonData.startedAt || 0;
      const end = seasonData.endedAt || Date.now();
      return ts >= start && ts <= end;
    });
  }
  return items;
}

function PlayerDetailModal({ player, onClose, allGames, allPlayers, playerEmojis = {}, user, userData, onUserDataChange, seasonConfig }) {
  const [activeTab, setActiveTab] = useState("elo"); // "elo", "game", "casino", "unboxing", "showcase", "achievements"
  const [casinoStats, setCasinoStats] = useState(null);
  const [playerAchievements, setPlayerAchievements] = useState(new Set());
  const [playerAchievementDocs, setPlayerAchievementDocs] = useState({});
  const [playerAchProgress, setPlayerAchProgress] = useState({});
  const [casinoTransactions, setCasinoTransactions] = useState([]);
  const [unboxingStats, setUnboxingStats] = useState(null);
  const [showcaseItems, setShowcaseItems] = useState([]);
  const [userInventory, setUserInventory] = useState([]);
  const [selectedGameForElo, setSelectedGameForElo] = useState(null);
  const [casinoGameFilter, setCasinoGameFilter] = useState('all');
  const [playerLevel, setPlayerLevel] = useState(null);
  const [profileTimeFilter, setProfileTimeFilter] = useState('all');
  const [eloSeasonFilter, setEloSeasonFilter] = useState(`season_${seasonConfig?.currentSeason || 2}`);
  const [editingShowcase, setEditingShowcase] = useState(false);
  const [pendingShowcase, setPendingShowcase] = useState([]);
  const [savingShowcase, setSavingShowcase] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [stockHoldings, setStockHoldings] = useState(null);
  const [stockTransactions, setStockTransactions] = useState([]);
  const [stockPrices, setStockPrices] = useState(null);
  const [sportsBets, setSportsBets] = useState(null);

  const playerGameCount = allGames.filter(g =>
    g.impostors.includes(player.id) || (g.crewmates && g.crewmates.includes(player.id))
  ).length;
  const rank = getRank(compositeScore(player, playerGameCount) ?? BASE_ELO);
  const equippedEmoji = playerEmojis[player.id];

  // Load casino and unboxing stats
  useEffect(() => {
    async function loadStats() {
      // Find user ID from player
      const usersSnapshot = await getDocs(collection(db, "users"));
      const userDoc = usersSnapshot.docs.find(doc => doc.data().claimedPlayer === player.id);

      if (userDoc) {
        const userId = userDoc.id;
        setProfileUserId(userId);

        // Load casino stats
        const casinoStatsDoc = await getDoc(doc(db, "casinoStats", userId));
        if (casinoStatsDoc.exists()) {
          setCasinoStats(casinoStatsDoc.data());
        }

        // Load casino transaction history for chart
        const txQuery = query(
          collection(db, "casinoTransactions"),
          where("userId", "==", userId),
          orderBy("timestamp", "asc")
        );
        const txSnap = await getDocs(txQuery);
        setCasinoTransactions(txSnap.docs.map(d => d.data()));

        // Load unboxing stats
        const unboxingStatsDoc = await getDoc(doc(db, "unboxingStats", userId));
        if (unboxingStatsDoc.exists()) {
          setUnboxingStats(unboxingStatsDoc.data());
        }

        // Load user inventory and showcase
        const userData = userDoc.data();
        setUserInventory(userData.lootboxInventory || []);
        setShowcaseItems(userData.showcaseItems || []);

        // Load player level
        if (userData.xp !== undefined) {
          setPlayerLevel(levelFromTotalXp(userData.xp || 0));
        }
      }

    }
    loadStats();
  }, [player.id]);

  // Load stock data when stocks tab is opened
  useEffect(() => {
    if (activeTab !== "stocks" || !profileUserId) return;
    async function loadStockData() {
      const [holdingsSnap, pricesSnap, txSnap] = await Promise.all([
        getDoc(doc(db, 'stockHoldings', profileUserId)),
        getDoc(doc(db, 'stockPrices', 'current')),
        getDocs(query(
          collection(db, 'stockTransactions'),
          where('userId', '==', profileUserId),
          orderBy('timestamp', 'desc')
        )),
      ]);
      if (holdingsSnap.exists()) setStockHoldings(holdingsSnap.data().holdings || {});
      else setStockHoldings({});
      if (pricesSnap.exists()) setStockPrices(pricesSnap.data().prices || {});
      setStockTransactions(txSnap.docs.map(d => ({ id: d.id, ...d.data() })).slice(0, 50));
    }
    loadStockData();
  }, [activeTab, profileUserId]);

  // Load sports bets when casino tab is opened
  useEffect(() => {
    if (activeTab !== "casino" || !profileUserId) return;
    async function loadSportsBets() {
      try {
        const snap = await getDocs(query(
          collection(db, 'sportsBets'),
          where('userId', '==', profileUserId),
          orderBy('placedAt', 'desc')
        ));
        setSportsBets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Failed to load sports bets:', err);
        setSportsBets([]);
      }
    }
    loadSportsBets();
  }, [activeTab, profileUserId]);

  // Real-time listeners for achievements
  useEffect(() => {
    const unsubPlayer = onSnapshot(doc(db, 'players', player.id), (snap) => {
      if (snap.exists()) {
        setPlayerAchProgress(snap.data().achievementProgress || {});
      }
    });
    const unsubAch = onSnapshot(collection(db, 'players', player.id, 'achievements'), (snap) => {
      const earned = new Set();
      const docs = {};
      snap.docs.forEach(d => {
        earned.add(d.id);
        docs[d.id] = d.data();
      });
      setPlayerAchievements(earned);
      setPlayerAchievementDocs(docs);
    });
    return () => {
      unsubPlayer();
      unsubAch();
    };
  }, [player.id]);

  // Filter games for non-ELO stats (ELO chart has its own season filter)
  const filteredGames = filterByTime(allGames, profileTimeFilter, seasonConfig);

  // Calculate win rates
  const imposterGames = filteredGames.filter(g => g.impostors.includes(player.id));
  const imposterWins = imposterGames.filter(g => g.didWin).length;
  const imposterWinRate = imposterGames.length > 0
    ? ((imposterWins / imposterGames.length) * 100).toFixed(1)
    : "N/A";

  const crewmateGames = filteredGames.filter(g =>
    (g.crewmates && g.crewmates.includes(player.id)) ||
    (!g.impostors.includes(player.id) && !g.crewmates)
  );
  const crewmateWins = crewmateGames.filter(g => !g.didWin).length;
  const crewmateWinRate = crewmateGames.length > 0
    ? ((crewmateWins / crewmateGames.length) * 100).toFixed(1)
    : "N/A";

  // Calculate streaks for each role
  function calculateModalRoleStreak(games, role) {
    // Count longest consecutive run of games where player was assigned as impostor or crewmate
    const sorted = [...games].sort((a, b) => a.timestamp - b.timestamp);
    let max = 0, cur = 0;
    for (const g of sorted) {
      const isImposter = g.impostors.includes(player.id);
      const matchesRole = role === 'imposter' ? isImposter : !isImposter;
      cur = matchesRole ? cur + 1 : 0;
      if (cur > max) max = cur;
    }
    return max;
  }

  function calculateModalStreak(games, role) {
    let filteredGames = [...games];

    if (role === 'imposter') {
      filteredGames = filteredGames.filter(g => g.impostors.includes(player.id));
    } else if (role === 'crewmate') {
      filteredGames = filteredGames.filter(g =>
        (g.crewmates && g.crewmates.includes(player.id)) ||
        (!g.impostors.includes(player.id) && !g.crewmates)
      );
    }

    const sorted = [...filteredGames].sort((a, b) => a.timestamp - b.timestamp);

    let currentStreak = 0;
    let maxWinStreak = 0;
    let maxLoseStreak = 0;
    let tempWinStreak = 0;
    let tempLoseStreak = 0;

    for (const game of sorted) {
      const isImposter = game.impostors.includes(player.id);
      const won = (isImposter && game.didWin) || (!isImposter && !game.didWin);

      if (won) {
        tempWinStreak++;
        tempLoseStreak = 0;
        if (tempWinStreak > maxWinStreak) maxWinStreak = tempWinStreak;
      } else {
        tempLoseStreak++;
        tempWinStreak = 0;
        if (tempLoseStreak > maxLoseStreak) maxLoseStreak = tempLoseStreak;
      }
    }
    currentStreak = tempWinStreak > 0 ? tempWinStreak : -tempLoseStreak;

    return { currentStreak, maxWinStreak, maxLoseStreak };
  }

  const playerGames = filteredGames
    .filter(g => g.impostors.includes(player.id) || (g.crewmates && g.crewmates.includes(player.id)))
    .sort((a, b) => b.timestamp - a.timestamp);

  // Calculate actual game count from games collection (more accurate than player.gameCount)
  const actualGameCount = playerGames.length;
  const gamesWon = playerGames.filter(g => {
    const isImposter = g.impostors.includes(player.id);
    return (isImposter && g.didWin) || (!isImposter && !g.didWin);
  }).length;

  const overallStreaks = calculateModalStreak(filteredGames, 'overall');
  const imposterStreaks = calculateModalStreak(filteredGames, 'imposter');
  const crewmateStreaks = calculateModalStreak(filteredGames, 'crewmate');
  const longestImposterRoleStreak = calculateModalRoleStreak(playerGames, 'imposter');
  const longestCrewmateRoleStreak = calculateModalRoleStreak(playerGames, 'crewmate');

  // Helper: find a player's playerStats entry in a game.
  // Uses eloChanges.id (resolved player doc ID) to find the player's raw plugin name,
  // then matches playerStats by that name. This is the same reliable path used by wins/ELO.
  function findPlayerStats(game, playerId) {
    if (!game.playerStats || !Array.isArray(game.playerStats)) return null;
    // First try: match via eloChanges which has the resolved id -> pluginName mapping
    if (game.eloChanges && Array.isArray(game.eloChanges)) {
      const eloEntry = game.eloChanges.find(e => e.id === playerId);
      if (eloEntry) {
        const ps = game.playerStats.find(p => p.playerName === eloEntry.name);
        if (ps) return ps;
      }
    }
    // Fallback: match by playerId stored directly on playerStats (new games after deploy)
    const byId = game.playerStats.find(p => p.playerId === playerId);
    if (byId) return byId;
    // Last resort: case-insensitive name match
    return game.playerStats.find(p => p.playerName && p.playerName.toLowerCase() === playerId.toLowerCase()) || null;
  }

  // Compute total kills from game logs (kills only, not ejects)
  const displayKills = filteredGames.reduce((sum, g) => {
    const ps = findPlayerStats(g, player.id);
    return sum + (ps && ps.isImpostor ? (ps.kills || 0) : 0);
  }, 0);

  // Compute avg task completion from game logs
  // Only count games where totalTasks > 0 (i.e. task data is available)
  let totalTasksCompleted = 0;
  let totalTasksAssigned = 0;
  filteredGames.forEach(g => {
    const ps = findPlayerStats(g, player.id);
    if (!ps || ps.isImpostor) return;
    const maxTasks = g.eloMetadata?.maxCrewTasks ||
      Math.max(0, ...(g.playerStats || []).filter(p => !p.isImpostor).map(p => p.tasksCompleted || 0));
    if (maxTasks <= 0) return; // skip games with no task data
    totalTasksCompleted += ps.tasksCompleted || 0;
    totalTasksAssigned += maxTasks;
  });
  const avgTaskCompletion = totalTasksAssigned > 0
    ? ((totalTasksCompleted / totalTasksAssigned) * 100).toFixed(1)
    : "N/A";

  // Calculate avg kill order as crewmate (crewmate-re-ranked death order)
  // Use filteredGames and match by playerStats name to avoid crewmateGames ID-mismatch issues
  let avgKillOrderTotal = 0;
  let avgKillOrderCount = 0;
  filteredGames.forEach(g => {
    const ps = findPlayerStats(g, player.id);
    if (!ps || ps.isImpostor || (ps.deathOrder || 0) === 0) return; // skip impostors and survivors
    const deadCrew = g.playerStats
      .filter(p => !p.isImpostor && (p.deathOrder || 0) > 0)
      .sort((a, b) => a.deathOrder - b.deathOrder);
    const crewRank = deadCrew.findIndex(p => p.playerName === ps.playerName) + 1;
    if (crewRank > 0) {
      avgKillOrderTotal += crewRank;
      avgKillOrderCount++;
    }
  });
  const avgKillOrder = avgKillOrderCount > 0 ? (avgKillOrderTotal / avgKillOrderCount).toFixed(2) : null;

  // Compute favorite kill — the player this person most often kills first
  const favoriteKillCounts = {};
  filteredGames.forEach(g => {
    if (!g.killEvents || !Array.isArray(g.killEvents)) return;
    // Resolve this player's plugin name from eloChanges
    let pluginName = null;
    if (g.eloChanges && Array.isArray(g.eloChanges)) {
      const entry = g.eloChanges.find(e => e.id === player.id);
      if (entry) pluginName = entry.name;
    }
    if (!pluginName) return;
    // Find kills by this player, sorted by deathOrder
    const myKills = g.killEvents
      .filter(k => k.killerName === pluginName)
      .sort((a, b) => (a.deathOrder || 0) - (b.deathOrder || 0));
    if (myKills.length === 0) return;
    const firstVictim = myKills[0].victimName;
    favoriteKillCounts[firstVictim] = (favoriteKillCounts[firstVictim] || 0) + 1;
  });
  let favoriteKill = null;
  let favoriteKillCount = 0;
  for (const [victim, count] of Object.entries(favoriteKillCounts)) {
    if (count > favoriteKillCount) {
      favoriteKillCount = count;
      favoriteKill = victim;
    }
  }

  function formatDate(timestamp) {
    return new Date(timestamp).toLocaleDateString();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content player-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&#x2715;</button>

        <div className="player-detail-header">
          <h2 style={{ color: rank.color }}>
            <img src={rank.icon} alt={rank.name} className="rank-badge-img-large" /> {player.name}
            {playerLevel && (
              <span className="player-level-badge">Lv.{playerLevel.level}</span>
            )}
          </h2>
          {playerLevel && (
            <div className="player-level-progress">
              <div className="player-level-bar">
                <div className="player-level-fill" style={{ width: `${playerLevel.xpToNext > 0 ? (playerLevel.currentXp / playerLevel.xpToNext * 100) : 100}%` }} />
              </div>
              <span className="player-level-xp-text">{playerLevel.currentXp}/{playerLevel.xpToNext} XP</span>
            </div>
          )}
          <div className="player-rank" style={{ color: rank.color }}>
            {rank.name} · {compositeScore(player, playerGameCount) || "Unranked"}
          </div>
          {equippedEmoji && (
            <div className="player-equipped-emoji">
              <span className="equipped-emoji-icon">
                {equippedEmoji.vfx && equippedEmoji.vfx.length > 0
                  ? <VfxSpan vfx={equippedEmoji.vfx}><ItemIcon item={equippedEmoji} size={36} /></VfxSpan>
                  : <ItemIcon item={equippedEmoji} size={36} />}
              </span>
              <div className="equipped-emoji-info">
                <span className="equipped-emoji-name" style={{ color: LOOTBOX_RARITIES[equippedEmoji.rarity]?.color || '#fff' }}>
                  {equippedEmoji.vfx && equippedEmoji.vfx.length > 0
                    ? <VfxSpan vfx={equippedEmoji.vfx}>{equippedEmoji.name}</VfxSpan>
                    : equippedEmoji.name}
                  {equippedEmoji.isStatTrak && <span style={{ marginLeft: '4px', fontSize: '14px', color: '#FF8C00' }}>&#x2122;</span>}
                  {equippedEmoji.vfx && equippedEmoji.vfx.length > 0 && (
                    <span style={{ marginLeft: '6px', color: '#a78bfa', fontWeight: 'bold', fontSize: '13px' }}>
                      ✦ {equippedEmoji.vfx.length > 1 ? `VFX x${equippedEmoji.vfx.length}` : 'VFX'}
                    </span>
                  )}
                </span>
                <span className="equipped-emoji-rarity" style={{ color: LOOTBOX_RARITIES[equippedEmoji.rarity]?.color || '#fff' }}>
                  {LOOTBOX_RARITIES[equippedEmoji.rarity]?.name || 'Unknown'}{equippedEmoji.isShiny ? ' ✨ SHINY' : ''}{equippedEmoji.isStatTrak ? ' 📊 STATTRAK' : ''}
                </span>
                {equippedEmoji.vfx && equippedEmoji.vfx.length > 0 && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: 'rgba(167, 139, 250, 0.08)',
                    border: '1px solid rgba(167, 139, 250, 0.3)',
                    borderRadius: '8px',
                    width: '100%'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#a78bfa', marginBottom: '8px' }}>
                      ✦ VFX Effects
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {(Array.isArray(equippedEmoji.vfx) ? equippedEmoji.vfx : [equippedEmoji.vfx]).map((effectId, idx) => {
                        const effect = VFX_EFFECTS.find(e => e.id === effectId);
                        return (
                          <div key={idx} style={{
                            padding: '4px 10px',
                            background: 'rgba(167, 139, 250, 0.15)',
                            border: '1px solid rgba(167, 139, 250, 0.3)',
                            borderRadius: '6px',
                            fontSize: '11px',
                            color: '#c4b5fd',
                            fontWeight: 'bold'
                          }}>
                            {effect ? effect.name : effectId}
                            {effect && <span style={{ color: '#888', marginLeft: '6px', fontWeight: 'normal' }}>— {effect.description}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {equippedEmoji.isStatTrak && equippedEmoji.statValues && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: 'rgba(255, 140, 0, 0.1)',
                    border: '1px solid #FF8C00',
                    borderRadius: '8px',
                    width: '100%'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#FF8C00', marginBottom: '8px' }}>
                      📊 StatTrak Stats:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '6px' }}>
                      {Object.entries(equippedEmoji.statValues).map(([stat, value]) => (
                        <div key={stat} style={{
                          padding: '4px 8px',
                          background: 'rgba(255, 140, 0, 0.15)',
                          border: '1px solid rgba(255, 140, 0, 0.3)',
                          borderRadius: '4px',
                          fontSize: '11px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{ color: 'var(--text-secondary)' }}>{STATTRAK_LABELS[stat] || stat}:</span>
                          <span style={{ color: '#FF8C00', fontWeight: 'bold' }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <SubTabs tabs={PROFILE_TABS} active={activeTab} onChange={setActiveTab} />

        {/* ELO Tab */}
        {activeTab === "elo" && (() => {
          // Season filter for ELO tab
          const eloFilters = getEloSeasonFilters(seasonConfig);
          const eloGames = filterByTime(allGames, eloSeasonFilter, seasonConfig)
            .filter(g => g.impostors.includes(player.id) || (g.crewmates && g.crewmates.includes(player.id)));

          // Build ELO history — three separate lines.
          // Imposter/Crewmate only plot on games where that role was played.
          // Overall is the running average of both ELOs after each game.
          const sortedGames = [...eloGames].sort((a, b) => a.timestamp - b.timestamp);
          // Derive starting ELOs by walking backwards from current values
          let curImpElo = player.eloImposter;
          let curCrewElo = player.eloCrewmate;
          for (let i = sortedGames.length - 1; i >= 0; i--) {
            const g = sortedGames[i];
            const entry = g.eloChanges && g.eloChanges.find(e => e.id === player.id);
            if (!entry) continue;
            if (g.impostors.includes(player.id)) curImpElo = Math.round(entry.oldElo);
            else curCrewElo = Math.round(entry.oldElo);
          }
          // Prepend game 0 as the starting point before any games were played
          const startImpElo = curImpElo;
          const startCrewElo = curCrewElo;
          const eloHistory = [{
            game: 0,
            date: 'Start',
            Overall: Math.round((startImpElo + startCrewElo) / 2),
            Imposter: startImpElo,
            Crewmate: startCrewElo,
          }];
          sortedGames.forEach((g, i) => {
            const entry = g.eloChanges && g.eloChanges.find(e => e.id === player.id);
            if (!entry) return;
            const isImposter = g.impostors.includes(player.id);
            if (isImposter) curImpElo = Math.round(entry.newElo);
            else curCrewElo = Math.round(entry.newElo);
            eloHistory.push({
              game: i + 1,
              date: new Date(g.timestamp).toLocaleDateString(),
              Overall: Math.round((curImpElo + curCrewElo) / 2),
              Imposter: isImposter ? curImpElo : null,
              Crewmate: !isImposter ? curCrewElo : null,
            });
          });

          // Calculate peak ELOs from history
          let peakImposter = startImpElo;
          let peakCrewmate = startCrewElo;
          let peakOverall = Math.round((startImpElo + startCrewElo) / 2);
          let trackImp = startImpElo;
          let trackCrew = startCrewElo;
          sortedGames.forEach(g => {
            const entry = g.eloChanges && g.eloChanges.find(e => e.id === player.id);
            if (!entry) return;
            if (g.impostors.includes(player.id)) trackImp = Math.round(entry.newElo);
            else trackCrew = Math.round(entry.newElo);
            if (trackImp > peakImposter) peakImposter = trackImp;
            if (trackCrew > peakCrewmate) peakCrewmate = trackCrew;
            const overall = Math.round((trackImp + trackCrew) / 2);
            if (overall > peakOverall) peakOverall = overall;
          });

          // Current ELO: use end-of-filtered-period values when a season filter is active
          const displayImpElo = eloHistory.length > 1
            ? eloHistory[eloHistory.length - 1].Imposter ?? (() => { for (let i = eloHistory.length - 1; i >= 0; i--) { if (eloHistory[i].Imposter != null) return eloHistory[i].Imposter; } return startImpElo; })()
            : Math.round(player.eloImposter);
          const displayCrewElo = eloHistory.length > 1
            ? eloHistory[eloHistory.length - 1].Crewmate ?? (() => { for (let i = eloHistory.length - 1; i >= 0; i--) { if (eloHistory[i].Crewmate != null) return eloHistory[i].Crewmate; } return startCrewElo; })()
            : Math.round(player.eloCrewmate);
          const displayOverallElo = eloHistory.length > 1
            ? eloHistory[eloHistory.length - 1].Overall
            : Math.round(compositeScore(player, playerGameCount));

          const showImpElo = displayImpElo;
          const showCrewElo = displayCrewElo;
          const showOverallElo = displayOverallElo;

          // Filtered partner stats for ELO tab
          const eloPlayerImpGames = eloGames.filter(g => g.impostors.includes(player.id));
          const eloPartnerStats = {};
          eloPlayerImpGames.forEach(game => {
            game.impostors.forEach(partnerId => {
              if (partnerId !== player.id) {
                if (!eloPartnerStats[partnerId]) eloPartnerStats[partnerId] = { wins: 0, losses: 0 };
                if (game.didWin) eloPartnerStats[partnerId].wins++;
                else eloPartnerStats[partnerId].losses++;
              }
            });
          });
          const eloPartners = Object.entries(eloPartnerStats).map(([id, stats]) => ({
            id,
            name: allPlayers.find(p => p.id === id)?.name || id,
            winRate: ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1),
            total: stats.wins + stats.losses
          })).filter(p => p.total >= 2).sort((a, b) => b.winRate - a.winRate);
          const eloBestPartner = eloPartners[0];
          const eloWorstPartner = eloPartners[eloPartners.length - 1];

          // Recent games for ELO tab (filtered by season)
          const eloRecentGames = [...eloGames]
            .filter(g => g.impostors.includes(player.id) || (g.crewmates && g.crewmates.includes(player.id)))
            .sort((a, b) => b.timestamp - a.timestamp);

          return (
            <>
              {/* ELO Season Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', color: '#888' }}>Filter:</span>
                {eloFilters.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setEloSeasonFilter(f.id)}
                    style={{
                      padding: '5px 12px',
                      background: eloSeasonFilter === f.id ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${eloSeasonFilter === f.id ? '#667eea' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '6px',
                      color: eloSeasonFilter === f.id ? '#667eea' : '#888',
                      fontWeight: eloSeasonFilter === f.id ? '700' : '500',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="player-elo-section">
                <div className="elo-card">
                  <div className="elo-label">🔪 Imposter ELO</div>
                  <div className="elo-value">{showImpElo}</div>
                  <div className="elo-sub">Peak: <span style={{ color: '#FFD700', fontWeight: 700 }}>{peakImposter}</span></div>
                </div>
                <div className="elo-card">
                  <div className="elo-label">🔵 Crewmate ELO</div>
                  <div className="elo-value">{showCrewElo}</div>
                  <div className="elo-sub">Peak: <span style={{ color: '#FFD700', fontWeight: 700 }}>{peakCrewmate}</span></div>
                </div>
                <div className="elo-card">
                  <div className="elo-label">⚖️ Overall ELO</div>
                  <div className="elo-value">{showOverallElo}</div>
                  <div className="elo-sub">Peak: <span style={{ color: '#FFD700', fontWeight: 700 }}>{peakOverall}</span></div>
                </div>
              </div>

              {eloHistory.length > 1 ? (
                <div style={{ marginTop: '24px' }}>
                  <h3 style={{ marginBottom: '12px' }}>📈 ELO History</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={eloHistory} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="game" label={{ value: 'Game #', position: 'insideBottom', offset: -2, fill: 'var(--text-secondary)', fontSize: 11 }} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                      <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} domain={['auto', 'auto']} />
                      <Tooltip
                        contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }}
                        labelFormatter={(label, payload) => {
                          const entry = eloHistory.find(e => e.game === label);
                          return entry && entry.date ? `Game #${label} · ${entry.date}` : `Game #${label}`;
                        }}
                        formatter={(value, name) => [value, name]}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                      {RANKS.filter(r => r.min > 0).map(r => (
                        <ReferenceLine
                          key={r.name}
                          y={r.min}
                          stroke={r.color}
                          strokeDasharray="4 3"
                          strokeOpacity={0.5}
                          label={{ value: r.name, position: 'insideTopRight', fill: r.color, fontSize: 9, fontWeight: 'bold' }}
                        />
                      ))}
                      <Line type="monotone" dataKey="Overall" stroke="#6495ED" strokeWidth={2} dot={eloHistory.length <= 100 ? { r: 2 } : false} connectNulls={true} />
                      <Line type="monotone" dataKey="Imposter" stroke="#FF6B6B" strokeWidth={2} dot={eloHistory.length <= 100 ? { r: 3 } : false} connectNulls={true} />
                      <Line type="monotone" dataKey="Crewmate" stroke="#4CAF50" strokeWidth={2} dot={eloHistory.length <= 100 ? { r: 3 } : false} connectNulls={true} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p style={{ color: 'var(--text-secondary)', marginTop: '16px', textAlign: 'center' }}>Not enough games to display ELO history.</p>
              )}

              {eloPartners.length > 0 && (
                <div className="partner-stats">
                  <h3>Partner Statistics</h3>
                  {eloBestPartner && (
                    <div className="partner-row best">
                      <span className="partner-label">Best Partner:</span>
                      <span className="partner-name">{eloBestPartner.name}</span>
                      <span className="partner-rate green">{eloBestPartner.winRate}% ({eloBestPartner.total} games)</span>
                    </div>
                  )}
                  {eloWorstPartner && eloWorstPartner.id !== eloBestPartner?.id && (
                    <div className="partner-row worst">
                      <span className="partner-label">Worst Partner:</span>
                      <span className="partner-name">{eloWorstPartner.name}</span>
                      <span className="partner-rate red">{eloWorstPartner.winRate}% ({eloWorstPartner.total} games)</span>
                    </div>
                  )}
                </div>
              )}

              <div className="recent-games">
                <h3>Recent Games</h3>
                <div className="recent-games-list">
                  {eloRecentGames.slice(0, 10).map((g, i) => {
                    const isImposter = g.impostors.includes(player.id);
                    const won = (isImposter && g.didWin) || (!isImposter && !g.didWin);
                    return (
                      <div key={i} className="recent-game-row" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className={`game-result ${won ? 'won' : 'lost'}`}>{won ? "✅" : "❌"}</span>
                        <span className="game-role" style={{ flex: 1 }}>{isImposter ? "🔪 Imposter" : "🔵 Crew"}</span>
                        <span className="game-date">{formatDate(g.timestamp)}</span>
                        {g.eloChanges && (
                          <button
                            onClick={() => setSelectedGameForElo(g)}
                            style={{ padding: '4px 8px', background: 'rgba(100,149,237,0.2)', border: '1px solid rgba(100,149,237,0.5)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#6495ED' }}
                            title="View ELO details"
                          >
                            ℹ️
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          );
        })()}

        {/* Game Stats Tab */}
        {activeTab === "game" && (() => {
          const pnl = { background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' };
          const pnlHead = (icon, text) => (
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>{icon}</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>{text}</span>
            </div>
          );
          const pnlBody = { padding: '16px 20px' };
          const statRow = (label, value, color = 'var(--text-primary)') => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{label}</span>
              <span style={{ fontSize: '15px', fontWeight: '700', color, fontFamily: "'Orbitron', sans-serif" }}>{value}</span>
            </div>
          );
          const overallWinRate = actualGameCount > 0 ? ((gamesWon / actualGameCount) * 100).toFixed(1) : 0;
          const impWinRate = imposterWinRate === 'N/A' ? 0 : parseFloat(imposterWinRate);
          const crewWinRate = crewmateWinRate === 'N/A' ? 0 : parseFloat(crewmateWinRate);
          const impFreq = actualGameCount > 0 ? ((imposterGames.length / actualGameCount) * 100).toFixed(1) : 0;

          return (
          <>
            {/* Time Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {getProfileTimeFilters(seasonConfig).map(f => (
                  <button
                    key={f.id}
                    onClick={() => setProfileTimeFilter(f.id)}
                    style={{
                      padding: '5px 14px',
                      background: profileTimeFilter === f.id ? 'rgba(232, 56, 74, 0.15)' : 'transparent',
                      border: `1px solid ${profileTimeFilter === f.id ? 'var(--red)' : 'var(--border)'}`,
                      borderRadius: '6px',
                      color: profileTimeFilter === f.id ? 'var(--red)' : 'var(--text-dim)',
                      fontWeight: profileTimeFilter === f.id ? '700' : '500',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
            </div>

            {/* Hero Stats Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '20px' }}>
              {[
                { label: 'Games', value: actualGameCount, color: '#FBBF24' },
                { label: 'Wins', value: gamesWon, color: '#4ADE80' },
                { label: 'Win Rate', value: `${overallWinRate}%`, color: '#5BC0DE' },
                { label: 'Streak', value: overallStreaks.currentStreak > 0 ? `W${overallStreaks.currentStreak}` : overallStreaks.currentStreak < 0 ? `L${Math.abs(overallStreaks.currentStreak)}` : '-', color: overallStreaks.currentStreak > 0 ? '#4ADE80' : overallStreaks.currentStreak < 0 ? '#E84855' : 'var(--text-dim)' },
                { label: 'Best Streak', value: overallStreaks.maxWinStreak, color: '#4ADE80' },
                { label: 'Worst Streak', value: overallStreaks.maxLoseStreak, color: '#E84855' },
              ].map((s, i) => (
                <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: `3px solid ${s.color}`, borderRadius: '8px', padding: '14px 16px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{s.label}</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-primary)', fontFamily: "'Orbitron', sans-serif" }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Impostor + Crewmate side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '20px' }}>
              {/* Impostor Panel */}
              <div style={pnl}>
                {pnlHead('🔪', 'Impostor')}
                <div style={pnlBody}>
                  {/* Win rate bar */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Win Rate</span>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#E84855' }}>{imposterWinRate}%</span>
                    </div>
                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(impWinRate, 100)}%`, background: '#E84855', borderRadius: '3px', transition: 'width 0.6s ease' }} />
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>{imposterWins}W / {imposterGames.length - imposterWins}L ({imposterGames.length} games)</div>
                  </div>
                  {statRow('Current Streak', imposterStreaks.currentStreak > 0 ? `W${imposterStreaks.currentStreak}` : imposterStreaks.currentStreak < 0 ? `L${Math.abs(imposterStreaks.currentStreak)}` : '-', imposterStreaks.currentStreak > 0 ? '#4ADE80' : imposterStreaks.currentStreak < 0 ? '#E84855' : 'var(--text-dim)')}
                  {statRow('Best Win Streak', imposterStreaks.maxWinStreak, '#4ADE80')}
                  {statRow('Most in a Row', longestImposterRoleStreak, '#E84855')}
                  {statRow('Total Kills', displayKills)}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Favorite Kill</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      {favoriteKill ? <>{favoriteKill} <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>({favoriteKillCount}x)</span></> : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Crewmate Panel */}
              <div style={pnl}>
                {pnlHead('🔵', 'Crewmate')}
                <div style={pnlBody}>
                  {/* Win rate bar */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Win Rate</span>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#3A8FD4' }}>{crewmateWinRate}%</span>
                    </div>
                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(crewWinRate, 100)}%`, background: '#3A8FD4', borderRadius: '3px', transition: 'width 0.6s ease' }} />
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>{crewmateWins}W / {crewmateGames.length - crewmateWins}L ({crewmateGames.length} games)</div>
                  </div>
                  {statRow('Current Streak', crewmateStreaks.currentStreak > 0 ? `W${crewmateStreaks.currentStreak}` : crewmateStreaks.currentStreak < 0 ? `L${Math.abs(crewmateStreaks.currentStreak)}` : '-', crewmateStreaks.currentStreak > 0 ? '#4ADE80' : crewmateStreaks.currentStreak < 0 ? '#E84855' : 'var(--text-dim)')}
                  {statRow('Best Win Streak', crewmateStreaks.maxWinStreak, '#4ADE80')}
                  {statRow('Most in a Row', longestCrewmateRoleStreak, '#3A8FD4')}
                  {statRow('Avg Tasks', avgTaskCompletion !== 'N/A' ? `${avgTaskCompletion}%` : 'N/A')}
                  {statRow('Avg Kill Order', avgKillOrder !== null ? `#${avgKillOrder}` : 'N/A')}
                </div>
              </div>
            </div>

            {/* Impostor Frequency */}
            <div style={pnl}>
              {pnlHead('🎲', 'Role Distribution')}
              <div style={pnlBody}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#E84855', fontWeight: '700' }}>Impostor</span>
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '700' }}>{impFreq}%</span>
                    </div>
                    <div style={{ height: '8px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
                      <div style={{ height: '100%', width: `${impFreq}%`, background: '#E84855', borderRadius: '4px 0 0 4px', transition: 'width 0.6s ease' }} />
                      <div style={{ height: '100%', flex: 1, background: '#3A8FD4', borderRadius: '0 4px 4px 0' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{imposterGames.length} games</span>
                      <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{crewmateGames.length} games</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
          );
        })()}

        {/* Casino Stats Tab */}
        {activeTab === "casino" && (() => {
          const CASINO_GAME_FILTERS = [
            { key: 'all', label: 'All Games' },
            { key: 'roulette', label: 'Roulette' },
            { key: 'slots', label: 'Slots' },
            { key: 'blackjack', label: 'Blackjack' },
            { key: 'poker', label: 'Texas Hold\'em' },
            { key: 'mines', label: 'Mines' },
            { key: 'keno', label: 'Keno' },
            { key: 'sportsbet', label: 'Sports' },
          ];

          // Always compute from transactions — aggregate casinoStats docs were reset at season launch
          const timeFilteredTx = filterByTimestamp(casinoTransactions, profileTimeFilter, seasonConfig);

          const filteredTx = casinoGameFilter === 'all'
            ? timeFilteredTx
            : timeFilteredTx.filter(tx => {
              const g = tx.game === 'slots-bonus-buy' ? 'slots' : tx.game;
              return g === casinoGameFilter;
            });

          const totalWagered = filteredTx.reduce((sum, tx) => sum + (tx.normalizedWagered || 0), 0);
          const totalNet = filteredTx.reduce((sum, tx) => sum + (tx.normalizedNet || 0), 0);
          const totalPlays = filteredTx.length;

          // Build cumulative net chart data from filtered transactions
          let running = 0;
          const chartData = filteredTx.map((tx, i) => {
            running += tx.normalizedNet;
            return { index: i + 1, net: parseFloat(running.toFixed(2)) };
          });

          const isUp = totalNet >= 0;

          const pnl = { background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' };
          const pnlHead = (icon, text) => (
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>{icon}</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>{text}</span>
            </div>
          );
          const pnlBody = { padding: '16px 20px' };

          // Compute additional stats
          const bestWin = filteredTx.length > 0 ? Math.max(0, ...filteredTx.map(tx => tx.normalizedNet || 0)) : 0;
          const worstLoss = filteredTx.length > 0 ? Math.min(0, ...filteredTx.map(tx => tx.normalizedNet || 0)) : 0;
          const slotsTx = filteredTx.filter(tx => (tx.game === 'slots' || tx.game === 'slots-bonus-buy'));
          const bestSlotsMult = slotsTx.reduce((max, tx) => Math.max(max, tx.multiplier || 0), 0);
          const minesTx = filteredTx.filter(tx => tx.game === 'mines');
          const bestMinesMult = minesTx.reduce((max, tx) => Math.max(max, tx.multiplier || 0), 0);
          const minesPlays = minesTx.length;
          const minesWins = minesTx.filter(tx => (tx.normalizedNet || 0) > 0).length;
          const minesWinRate = minesPlays > 0 ? Math.round((minesWins / minesPlays) * 100) : 0;
          const minesWinMultis = minesTx.filter(tx => (tx.multiplier || 0) > 0);
          const minesAvgMulti = minesWinMultis.length > 0 ? parseFloat((minesWinMultis.reduce((sum, tx) => sum + tx.multiplier, 0) / minesWinMultis.length).toFixed(2)) : 0;
          const kenoTx = filteredTx.filter(tx => tx.game === 'keno');
          const bestKenoMult = kenoTx.reduce((max, tx) => Math.max(max, tx.multiplier || 0), 0);
          const kenoPlays = kenoTx.length;
          const kenoWins = kenoTx.filter(tx => (tx.normalizedNet || 0) > 0).length;
          const kenoWinRate = kenoPlays > 0 ? Math.round((kenoWins / kenoPlays) * 100) : 0;
          let bjCount = filteredTx.filter(tx => tx.game === 'blackjack' && tx.isBlackjack).length;
          if (bjCount === 0 && profileTimeFilter === 'all') bjCount = casinoStats?.blackjack?.blackjacks || 0;

          return (
            <>
              {/* Filters */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {getProfileTimeFilters(seasonConfig).map(f => (
                  <button
                    key={f.id}
                    onClick={() => setProfileTimeFilter(f.id)}
                    style={{
                      padding: '5px 14px',
                      background: profileTimeFilter === f.id ? 'rgba(232, 56, 74, 0.15)' : 'transparent',
                      border: `1px solid ${profileTimeFilter === f.id ? 'var(--red)' : 'var(--border)'}`,
                      borderRadius: '6px',
                      color: profileTimeFilter === f.id ? 'var(--red)' : 'var(--text-dim)',
                      fontWeight: profileTimeFilter === f.id ? '700' : '500',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {CASINO_GAME_FILTERS.map(f => (
                  <button key={f.key} onClick={() => setCasinoGameFilter(f.key)} style={{
                    padding: '5px 14px',
                    background: casinoGameFilter === f.key ? 'rgba(232, 56, 74, 0.15)' : 'transparent',
                    border: `1px solid ${casinoGameFilter === f.key ? 'var(--red)' : 'var(--border)'}`,
                    borderRadius: '6px',
                    color: casinoGameFilter === f.key ? 'var(--red)' : 'var(--text-dim)',
                    fontWeight: casinoGameFilter === f.key ? '700' : '500',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Hero Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                {[
                  { label: 'Wagered', value: parseFloat(totalWagered.toFixed(2)).toLocaleString(), color: '#FBBF24' },
                  { label: 'Net P/L', value: `${isUp ? '+' : ''}${parseFloat(totalNet.toFixed(2)).toLocaleString()}`, color: isUp ? '#4ADE80' : '#E84855' },
                  { label: 'Wagers', value: totalPlays, color: '#5BC0DE' },
                ].map((s, i) => (
                  <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: `3px solid ${s.color}`, borderRadius: '8px', padding: '14px 16px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{s.label}</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-primary)', fontFamily: "'Orbitron', sans-serif" }}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* Highlights Panel */}
              {filteredTx.length > 0 && (
                <div style={{ ...pnl, marginBottom: '20px' }}>
                  {pnlHead('🎰', casinoGameFilter === 'all' ? 'Highlights' : `${CASINO_GAME_FILTERS.find(f => f.key === casinoGameFilter)?.label} Highlights`)}
                  <div style={pnlBody}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                      {bestWin > 0 && (
                        <div style={{ background: 'rgba(74, 222, 128, 0.08)', border: '1px solid rgba(74, 222, 128, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Best Win</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#4ADE80', fontFamily: "'Orbitron', sans-serif" }}>+{parseFloat(bestWin.toFixed(2)).toLocaleString()}</div>
                        </div>
                      )}
                      {worstLoss < 0 && (
                        <div style={{ background: 'rgba(232, 72, 85, 0.08)', border: '1px solid rgba(232, 72, 85, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Worst Loss</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#E84855', fontFamily: "'Orbitron', sans-serif" }}>{parseFloat(worstLoss.toFixed(2)).toLocaleString()}</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'slots') && bestSlotsMult > 0 && (
                        <div style={{ background: 'rgba(156, 39, 176, 0.08)', border: '1px solid rgba(156, 39, 176, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Slots Multi</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#9C27B0', fontFamily: "'Orbitron', sans-serif" }}>{bestSlotsMult}x</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'blackjack') && bjCount > 0 && (
                        <div style={{ background: 'rgba(34, 211, 238, 0.08)', border: '1px solid rgba(34, 211, 238, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Blackjacks</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#22D3EE', fontFamily: "'Orbitron', sans-serif" }}>{bjCount}</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'mines') && bestMinesMult > 0 && (
                        <div style={{ background: 'rgba(255, 152, 0, 0.08)', border: '1px solid rgba(255, 152, 0, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Best Mines Multi</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#FF9800', fontFamily: "'Orbitron', sans-serif" }}>{bestMinesMult}x</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'mines') && minesPlays > 0 && (
                        <div style={{ background: 'rgba(255, 152, 0, 0.08)', border: '1px solid rgba(255, 152, 0, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Mines Played</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#FF9800', fontFamily: "'Orbitron', sans-serif" }}>{minesPlays}</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'mines') && minesPlays > 0 && (
                        <div style={{ background: 'rgba(255, 152, 0, 0.08)', border: '1px solid rgba(255, 152, 0, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Mines Win Rate</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#FF9800', fontFamily: "'Orbitron', sans-serif" }}>{minesWinRate}%</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'mines') && minesAvgMulti > 0 && (
                        <div style={{ background: 'rgba(255, 152, 0, 0.08)', border: '1px solid rgba(255, 152, 0, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Avg Cashout Multi</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#FF9800', fontFamily: "'Orbitron', sans-serif" }}>{minesAvgMulti}x</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'keno') && bestKenoMult > 0 && (
                        <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Best Keno Multi</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#F59E0B', fontFamily: "'Orbitron', sans-serif" }}>{bestKenoMult}x</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'keno') && kenoPlays > 0 && (
                        <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Keno Played</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#F59E0B', fontFamily: "'Orbitron', sans-serif" }}>{kenoPlays}</div>
                        </div>
                      )}
                      {(casinoGameFilter === 'all' || casinoGameFilter === 'keno') && kenoPlays > 0 && (
                        <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Keno Win Rate</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#F59E0B', fontFamily: "'Orbitron', sans-serif" }}>{kenoWinRate}%</div>
                        </div>
                      )}
                      {(() => {
                        if (casinoGameFilter !== 'all' && casinoGameFilter !== 'sportsbet') return null;
                        if (!sportsBets || sportsBets.length === 0) return null;
                        const sWon = sportsBets.filter(b => b.status === 'won');
                        const sSettled = sportsBets.filter(b => b.status === 'won' || b.status === 'lost');
                        const sWinRate = sSettled.length > 0 ? ((sWon.length / sSettled.length) * 100).toFixed(0) : 0;
                        const sParlays = sportsBets.filter(b => b.type === 'parlay' && b.status === 'won').length;
                        return (
                          <>
                            {sSettled.length > 0 && (
                              <div style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Bets Record</div>
                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#38BDF8', fontFamily: "'Orbitron', sans-serif" }}>{sWon.length}W-{sSettled.length - sWon.length}L ({sWinRate}%)</div>
                              </div>
                            )}
                            {sParlays > 0 && (
                              <div style={{ background: 'rgba(240, 192, 64, 0.08)', border: '1px solid rgba(240, 192, 64, 0.2)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Parlays Won</div>
                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#F0C040', fontFamily: "'Orbitron', sans-serif" }}>{sParlays}</div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Cumulative Net Chart */}
              {chartData.length > 1 && (
                <div style={{ ...pnl, marginBottom: '20px' }}>
                  {pnlHead('📈', 'Cumulative Net')}
                  <div style={pnlBody}>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="index" label={{ value: 'Wager #', position: 'insideBottom', offset: -2, fill: 'var(--text-secondary)', fontSize: 11 }} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                        <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} domain={['auto', 'auto']} />
                        <Tooltip
                          contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(value) => [`${value > 0 ? '+' : ''}${parseFloat(value.toFixed(2))} 🔵`, 'Cumulative Net']}
                          labelFormatter={(label) => `Wager #${label}`}
                        />
                        <ReferenceLine y={0} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" />
                        <Line
                          type="monotone"
                          dataKey="net"
                          stroke={chartData[chartData.length - 1]?.net >= 0 ? '#4CAF50' : '#f44336'}
                          dot={false}
                          strokeWidth={2}
                          name="Net"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Sports Bets Detail — shown when sportsbet filter active */}
              {casinoGameFilter === 'sportsbet' && sportsBets && sportsBets.length > 0 && (
                <div style={{ ...pnl, marginBottom: '20px' }}>
                  {pnlHead('🏈', 'Recent Bets')}
                  <div style={pnlBody}>
                    {sportsBets.slice(0, 15).map(b => {
                      const legCount = b.legs ? b.legs.length : 0;
                      const label = b.type === 'parlay'
                        ? `${legCount}-leg parlay`
                        : (b.legs && b.legs[0]
                          ? `${b.legs[0].market || 'Bet'} · ${b.legs[0].selection || ''}`
                          : 'Straight bet');
                      const odds = b.combinedOdds != null
                        ? (b.combinedOdds > 0 ? `+${b.combinedOdds}` : `${b.combinedOdds}`)
                        : '';
                      const tokenIcon = b.currency === 'impostor' ? '🔴' : '🔵';
                      const statusColors = {
                        won:       { bg: 'rgba(74,222,128,0.15)',  fg: '#4ADE80' },
                        lost:      { bg: 'rgba(239,68,68,0.15)',   fg: '#EF4444' },
                        active:    { bg: 'rgba(56,189,248,0.15)',  fg: '#38BDF8' },
                        push:      { bg: 'rgba(240,192,64,0.15)',  fg: '#F0C040' },
                        cancelled: { bg: 'rgba(255,255,255,0.05)', fg: 'var(--text-secondary)' },
                      };
                      const sc = statusColors[b.status] || statusColors.cancelled;
                      const displayNet = b.status === 'won'
                        ? (b.actualPayout || 0) - (b.wagerTokens || 0)
                        : b.status === 'lost'
                          ? -(b.wagerTokens || 0)
                          : null;
                      const fmtDate = (ts) => {
                        if (!ts) return '';
                        const d = new Date(ts);
                        return `${d.getMonth() + 1}/${d.getDate()}`;
                      };
                      return (
                        <div key={b.id} className="stock-position-card">
                          <div className="stock-position-left">
                            <span className="stock-position-icon">🏈</span>
                            <div className="stock-position-info">
                              <span className="stock-position-name">{label}</span>
                              <span className="stock-position-shares">
                                {b.wagerTokens} {tokenIcon} {odds && `@ ${odds}`} · {fmtDate(b.placedAt)}
                              </span>
                            </div>
                          </div>
                          <div className="stock-position-right">
                            <span style={{
                              padding: '2px 8px', borderRadius: 4,
                              fontSize: 10, fontWeight: 700,
                              fontFamily: "'Orbitron', sans-serif",
                              letterSpacing: '0.04em', textTransform: 'uppercase',
                              background: sc.bg, color: sc.fg,
                            }}>{b.status}</span>
                            {displayNet != null && (
                              <span className={`stock-position-pnl ${displayNet >= 0 ? 'positive' : 'negative'}`} style={{ display: 'block', marginTop: 4 }}>
                                {displayNet >= 0 ? '+' : ''}{displayNet.toFixed(2)} 🪙
                              </span>
                            )}
                            {b.status === 'active' && (
                              <span className="stock-position-pnl" style={{ display: 'block', marginTop: 4, color: 'var(--text-secondary)' }}>
                                to win {(b.potentialPayout - b.wagerTokens).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {filteredTx.length === 0 && (
                <div style={{ padding: '48px 0', textAlign: 'center' }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎰</div>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                    {casinoGameFilter === 'all' ? 'No casino activity recorded yet.' : `No ${CASINO_GAME_FILTERS.find(f => f.key === casinoGameFilter)?.label} activity recorded yet.`}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Unboxing Stats Tab */}
        {activeTab === "unboxing" && (
          unboxingStats ? (() => {
            // When time-filtered, compute stats from inventory items instead of aggregate doc
            const isUnboxTimeFiltered = profileTimeFilter !== 'all';
            const filteredItems = isUnboxTimeFiltered
              ? filterByTimestamp(userInventory, profileTimeFilter, seasonConfig, 'unboxedAt')
              : null;

            const rc = isUnboxTimeFiltered
              ? filteredItems.reduce((acc, item) => { acc[item.rarity?.toLowerCase()] = (acc[item.rarity?.toLowerCase()] || 0) + 1; return acc; }, {})
              : (unboxingStats.rarityCounts || {});
            const total = isUnboxTimeFiltered ? filteredItems.length : (unboxingStats.totalUnboxed || 0);
            const shinyCount = isUnboxTimeFiltered ? filteredItems.filter(i => i.isShiny).length : (unboxingStats.shinyCount || 0);
            const statTrakCount = isUnboxTimeFiltered ? filteredItems.filter(i => i.isStatTrak).length : (unboxingStats.statTrakCount || 0);
            const vfxCount = isUnboxTimeFiltered ? filteredItems.filter(i => i.vfx && i.vfx.length > 0).length : (unboxingStats.vfxCount || 0);
            const lc = isUnboxTimeFiltered ? {} : (unboxingStats.lootboxTypeCounts || {});
            const boxLabels = { normal: '🎁 Normal', elite: '👑 Elite', god: '⚡ God Box', shiny: '✨ Shiny', stattrak: '📊 StatTrak', vfxbox: '✦ VFX' };
            return (
              <div>
                {/* Time Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#888' }}>Filter:</span>
                  {getProfileTimeFilters(seasonConfig).map(f => (
                    <button
                      key={f.id}
                      onClick={() => setProfileTimeFilter(f.id)}
                      style={{
                        padding: '5px 12px',
                        background: profileTimeFilter === f.id ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${profileTimeFilter === f.id ? '#667eea' : 'rgba(255,255,255,0.1)'}`,
                        borderRadius: '6px',
                        color: profileTimeFilter === f.id ? '#667eea' : '#888',
                        fontWeight: profileTimeFilter === f.id ? '700' : '500',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                  {[
                    { label: 'Total Unboxed', value: total },
                    { label: 'Shiny', value: shinyCount, color: '#FFD700' },
                    { label: 'StatTrak', value: statTrakCount, color: '#FF8C00' },
                    { label: 'VFX', value: vfxCount, color: '#a78bfa' },
                  ].map((s, i) => (
                    <div key={i} style={{ background: 'var(--bg-elevated)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border)', textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '6px' }}>{s.label}</div>
                      <div style={{ fontSize: '20px', fontWeight: '800', color: s.color || 'var(--text-primary)' }}>{s.value.toLocaleString()}</div>
                      {total > 0 && s.label !== 'Total Unboxed' && (
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{((s.value / total) * 100).toFixed(1)}%</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Rarity Breakdown */}
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>Rarity Breakdown</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px', marginBottom: '24px' }}>
                  {getRarityTiers().map(r => {
                    const count = rc[r.toLowerCase()] || 0;
                    const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
                    const color = LOOTBOX_RARITIES[r]?.color || '#888';
                    return (
                      <div key={r} style={{ padding: '10px', borderRadius: '8px', background: `${color}10`, border: `1px solid ${color}30`, textAlign: 'center' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color, textTransform: 'uppercase', marginBottom: '4px' }}>{r}</div>
                        <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>{count}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{pct}%</div>
                      </div>
                    );
                  })}
                </div>

                {/* Box Types — only available for all-time (aggregate stats) */}
                {!isUnboxTimeFiltered && (
                  <>
                    <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>Boxes Opened</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px' }}>
                      {Object.entries(boxLabels).map(([key, label]) => (
                        <div key={key} style={{ padding: '10px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', textAlign: 'center' }}>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{label}</div>
                          <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>{(lc[key] || 0).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })() : (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>No unboxing data yet</div>
            </div>
          )
        )}

        {/* Showcase Tab */}
        {activeTab === "showcase" && (() => {
          const isOwnProfile = user && profileUserId && user.uid === profileUserId;
          const inventory = isOwnProfile ? (userData?.lootboxInventory || []) : userInventory;

          // Top 5 most valuable items
          const valuedItems = inventory
            .map(item => ({ ...item, value: getS2ItemValue(item) }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 5);

          // Showcase items (user picks)
          const displayShowcase = editingShowcase ? pendingShowcase : showcaseItems;

          const itemKey = (item) => `${item.id}_${item.unboxedAt || item.obtainedAt}_${item.isShiny || false}_${item.isStatTrak || false}`;

          const togglePendingItem = (item) => {
            const key = itemKey(item);
            const exists = pendingShowcase.find(i => itemKey(i) === key);
            if (exists) {
              setPendingShowcase(pendingShowcase.filter(i => itemKey(i) !== key));
            } else if (pendingShowcase.length < 5) {
              setPendingShowcase([...pendingShowcase, item]);
            }
          };

          const saveShowcase = async () => {
            if (!user || !isOwnProfile) return;
            setSavingShowcase(true);
            try {
              const cleanItems = pendingShowcase.map(({ value, ...rest }) => rest);
              await saveShowcaseItems(cleanItems);
              setShowcaseItems(cleanItems);
              setEditingShowcase(false);
              if (onUserDataChange) onUserDataChange();
            } catch (e) {
              console.error('Failed to save showcase:', e);
            }
            setSavingShowcase(false);
          };

          const rarities = getLootboxRarities();

          const renderShowcaseCard = (item, idx, showValue = false) => {
            const rarity = rarities[item.rarity] || LOOTBOX_RARITIES[item.rarity] || { color: '#888', name: item.rarity };
            const val = getS2ItemValue(item);
            return (
              <div key={idx} className={`showcase-card ${rarity.glow ? 'glow' : ''}`}
                style={{ borderColor: rarity.color, boxShadow: rarity.glow ? `0 0 12px ${rarity.color}40` : 'none' }}>
                <div className="showcase-card-icon">
                  <ItemIcon item={item} size={40} />
                </div>
                <div className="showcase-card-name" style={{ color: rarity.color }}>{item.name}</div>
                <div className="showcase-card-rarity">{rarity.name}</div>
                {item.isShiny && <span className="showcase-badge shiny">Shiny</span>}
                {item.isStatTrak && <span className="showcase-badge stattrak">ST</span>}
                {item.vfx && item.vfx.length > 0 && <span className="showcase-badge vfx">VFX</span>}
                {showValue && <div className="showcase-card-value">${val.toFixed(2)}</div>}
              </div>
            );
          };

          // For picker: group by rarity
          const rarityOrder = ['TESTER', 'GALAXY', 'MYTHIC', 'LEGENDARY', 'EPIC', 'RARE', 'UNCOMMON', 'COMMON'];

          return (
            <div className="showcase-tab">
              {/* Top 5 Most Valuable */}
              <div className="showcase-section">
                <h3 className="showcase-section-title">Top 5 Most Valuable</h3>
                {valuedItems.length > 0 ? (
                  <div className="showcase-grid">
                    {valuedItems.map((item, i) => renderShowcaseCard(item, i, true))}
                  </div>
                ) : (
                  <div className="showcase-empty">No items in inventory</div>
                )}
              </div>

              {/* Player's Picks */}
              <div className="showcase-section">
                <div className="showcase-section-header">
                  <h3 className="showcase-section-title">Player's Picks</h3>
                  {isOwnProfile && !editingShowcase && (
                    <button className="showcase-edit-btn" onClick={() => {
                      setPendingShowcase([...showcaseItems]);
                      setEditingShowcase(true);
                    }}>Edit</button>
                  )}
                  {isOwnProfile && editingShowcase && (
                    <div className="showcase-edit-actions">
                      <button className="showcase-save-btn" onClick={saveShowcase} disabled={savingShowcase}>
                        {savingShowcase ? 'Saving...' : 'Save'}
                      </button>
                      <button className="showcase-cancel-btn" onClick={() => setEditingShowcase(false)}>Cancel</button>
                    </div>
                  )}
                </div>

                {!editingShowcase ? (
                  displayShowcase.length > 0 ? (
                    <div className="showcase-grid">
                      {displayShowcase.map((item, i) => renderShowcaseCard(item, i, true))}
                    </div>
                  ) : (
                    <div className="showcase-empty">
                      {isOwnProfile ? 'Click Edit to select up to 5 items to display' : 'No items selected'}
                    </div>
                  )
                ) : (
                  <>
                    <div className="showcase-selected-label">
                      Selected: {pendingShowcase.length}/5
                    </div>
                    {pendingShowcase.length > 0 && (
                      <div className="showcase-grid" style={{ marginBottom: '16px' }}>
                        {pendingShowcase.map((item, i) => (
                          <div key={i} className="showcase-card selected" onClick={() => togglePendingItem(item)}
                            style={{ borderColor: (rarities[item.rarity] || { color: '#888' }).color, cursor: 'pointer' }}>
                            <div className="showcase-remove-badge">x</div>
                            <div className="showcase-card-icon"><ItemIcon item={item} size={40} /></div>
                            <div className="showcase-card-name" style={{ color: (rarities[item.rarity] || { color: '#888' }).color }}>{item.name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="showcase-picker">
                      {rarityOrder.map(rarity => {
                        const items = inventory.filter(i => i.rarity === rarity);
                        if (items.length === 0) return null;
                        const rarityInfo = rarities[rarity] || { name: rarity, color: '#888' };
                        return (
                          <div key={rarity} className="showcase-picker-group">
                            <div className="showcase-picker-rarity" style={{ color: rarityInfo.color }}>{rarityInfo.name}</div>
                            <div className="showcase-picker-items">
                              {items.map((item, i) => {
                                const isSelected = pendingShowcase.some(s => itemKey(s) === itemKey(item));
                                return (
                                  <div key={i}
                                    className={`showcase-picker-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => togglePendingItem(item)}
                                    style={{ borderColor: isSelected ? rarityInfo.color : 'var(--border)', cursor: 'pointer' }}>
                                    <ItemIcon item={item} size={28} />
                                    <span className="showcase-picker-item-name">{item.name}</span>
                                    {item.isShiny && <span className="showcase-badge shiny" style={{ fontSize: '9px', padding: '1px 4px' }}>S</span>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* Achievements Tab */}
        {activeTab === "achievements" && (
          <div className="modal-achievements-tab">
            <div className="achievements-summary" style={{ marginBottom: '16px' }}>
              <span className="achievements-count" style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {playerAchievements.size} / {getTotalAchievements()} Achievements
              </span>
            </div>
            {Object.entries(getAchievementCategories()).map(([catKey, cat]) => (
              <AchievementCategory
                key={catKey}
                catKey={catKey}
                cat={cat}
                progress={playerAchProgress}
                achievements={playerAchievements}
                achievementDocs={playerAchievementDocs}
              />
            ))}
          </div>
        )}

        {activeTab === "stocks" && (
          <div className="modal-stocks-tab">
            {!stockHoldings ? (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>Loading stock data...</p>
            ) : Object.keys(stockHoldings).length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>No stock positions yet.</p>
            ) : (
              <>
                {/* Portfolio summary */}
                {(() => {
                  let totalValue = 0;
                  let totalCost = 0;
                  const positions = [];
                  for (const [stockId, holding] of Object.entries(stockHoldings)) {
                    if (holding.shares <= 0) continue;
                    const stock = STOCKS.find(s => s.id === stockId);
                    if (!stock) continue;
                    const price = stockPrices?.[stock.ticker]?.price || 0;
                    const value = holding.shares * price;
                    totalValue += value;
                    totalCost += holding.costBasis;
                    positions.push({ stock, holding, price, value, pnl: value - holding.costBasis });
                  }
                  positions.sort((a, b) => b.value - a.value);
                  const totalPnL = totalValue - totalCost;
                  const totalPnLPct = totalCost > 0 ? ((totalPnL / totalCost) * 100) : 0;

                  return (
                    <div>
                      <div className="stock-portfolio-summary">
                        <div className="stock-portfolio-stat">
                          <span className="stock-portfolio-stat-label">Portfolio Value</span>
                          <span className="stock-portfolio-stat-value">{totalValue.toFixed(2)} 🔵</span>
                        </div>
                        <div className="stock-portfolio-stat">
                          <span className="stock-portfolio-stat-label">Total Invested</span>
                          <span className="stock-portfolio-stat-value">{totalCost.toFixed(2)} 🔵</span>
                        </div>
                        <div className="stock-portfolio-stat">
                          <span className="stock-portfolio-stat-label">Total P&L</span>
                          <span className={`stock-portfolio-stat-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
                            {totalPnL >= 0 ? '+' : ''}{totalPnL.toFixed(2)} ({totalPnLPct.toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                      <h4 className="stock-portfolio-positions-title" style={{ marginTop: '16px' }}>Holdings</h4>
                      {positions.map(({ stock, holding, price, value, pnl }) => {
                        const pnlPct = holding.costBasis > 0 ? ((pnl / holding.costBasis) * 100) : 0;
                        return (
                          <div key={stock.id} className="stock-position-card">
                            <div className="stock-position-left">
                              <span className="stock-position-icon">{stock.icon}</span>
                              <div className="stock-position-info">
                                <span className="stock-position-name">{stock.name}</span>
                                <span className="stock-position-shares">
                                  {holding.shares.toFixed(4)} shares @ {holding.avgPrice.toFixed(2)}
                                </span>
                              </div>
                            </div>
                            <div className="stock-position-right">
                              <span className="stock-position-value">{value.toFixed(2)} 🔵</span>
                              <span className={`stock-position-pnl ${pnl >= 0 ? 'positive' : 'negative'}`}>
                                {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} ({pnlPct.toFixed(1)}%)
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Recent trades */}
                {stockTransactions.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h4 className="stock-portfolio-positions-title">Recent Trades</h4>
                    {stockTransactions.slice(0, 10).map(tx => {
                      const stock = STOCKS.find(s => s.id === tx.stockId);
                      return (
                        <div key={tx.id} className="stock-position-card">
                          <div className="stock-position-left">
                            <span className="stock-position-icon">{stock?.icon || '📈'}</span>
                            <div className="stock-position-info">
                              <span className="stock-position-name">
                                {tx.type === 'buy' ? 'Bought' : 'Sold'} {stock?.name || tx.stockId}
                              </span>
                              <span className="stock-position-shares">
                                {tx.shares.toFixed(4)} shares @ {tx.pricePerShare.toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="stock-position-right">
                            <span className="stock-position-value">
                              {tx.type === 'buy' ? '-' : '+'}{tx.totalCost.toFixed(2)} 🔵
                            </span>
                            {tx.type === 'sell' && tx.profit !== undefined && (
                              <span className={`stock-position-pnl ${tx.profit >= 0 ? 'positive' : 'negative'}`}>
                                {tx.profit >= 0 ? '+' : ''}{tx.profit.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>

      {selectedGameForElo && (
        <EloDetailsModal
          game={selectedGameForElo}
          players={allPlayers}
          onClose={() => setSelectedGameForElo(null)}
        />
      )}
    </div>
  );
}

export default PlayerDetailModal;
